import React, { useEffect, useRef, useState } from 'react';
import Seo from '@/components/seo/Seo';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheck, FaImage } from 'react-icons/fa';
import { getOrder } from '@/api/orders';
import { useCart } from '@/context/CartContext';
import { trackPurchase } from '@/lib/analytics';
import { formatPrice } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { ErrorState, LoadingState } from '@/modules/shop/components/ShopStates';

/**
 * Stranica uspeha. Podatke uzima iz navigacionog state-a (odmah posle slanja), a na
 * osvežavanje ih dovlači sa `GET /orders/{broj}?token=` — zato token stoji u URL-u.
 */
const OrderSuccessPage = () => {
  const { orderNumber } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { clearCart } = useCart();

  const token = searchParams.get('token');
  const [order, setOrder] = useState(location.state?.order || null);
  const [status, setStatus] = useState(location.state?.order ? 'ready' : 'loading');

  /** Konverzija sme da se okine tačno jednom po porudžbini — StrictMode montira dvaput. */
  const trackedRef = useRef(false);

  useEffect(() => {
    if (order || !token) {
      if (!order && !token) setStatus('error');
      return undefined;
    }
    let active = true;
    getOrder(orderNumber, token)
      .then((data) => {
        if (!active) return;
        setOrder(data);
        setStatus('ready');
      })
      .catch(() => active && setStatus('error'));
    return () => {
      active = false;
    };
  }, [order, orderNumber, token]);

  useEffect(() => {
    if (!order || trackedRef.current) return;
    trackedRef.current = true;
    clearCart();
    trackPurchase({ transactionId: order.orderNumber, value: Number(order.total), currency: 'RSD' });
  }, [order, clearCart]);

  if (status === 'loading') {
    return (
      <div className="pt-32 min-h-[60vh]">
        <LoadingState label="Učitavanje porudžbine…" />
      </div>
    );
  }

  if (status === 'error' || !order) {
    return (
      <div className="pt-32 min-h-[60vh]">
        <Seo title="Porudžbina" noindex />
        <ErrorState message="Porudžbina nije pronađena. Proverite link iz potvrde ili nas kontaktirajte." />
      </div>
    );
  }

  const shippingLabel = Number(order.shippingCost) === 0
    ? 'dogovara se prilikom potvrde'
    : formatPrice(order.shippingCost);

  return (
    <>
      <Seo title={`Porudžbina ${order.orderNumber}`} noindex nofollow />

      <div className="pt-32 pb-20 min-h-[60vh]" data-testid="order-success-page">
        <div className="container-custom max-w-3xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success/10 flex items-center justify-center">
              <FaCheck className="text-success" size={26} />
            </div>
            <h1 className="font-heading text-4xl text-brand-primary mb-3">Hvala na porudžbini!</h1>
            <p className="text-neutral-500">
              Broj porudžbine:{' '}
              <strong className="text-brand-primary" data-testid="order-number">{order.orderNumber}</strong>
            </p>
            <p className="text-neutral-500 mt-2">
              Potvrdu smo poslali na Vaš email. Kontaktiraćemo Vas radi dogovora oko dostave.
            </p>
          </motion.div>

          <div className="bg-neutral-50 p-8">
            <h2 className="font-heading text-xl text-brand-primary mb-6">Pregled porudžbine</h2>

            <div className="space-y-4 mb-6">
              {order.items.map((item, index) => (
                <div key={`${item.productName}-${index}`} className="flex items-center gap-4 text-sm">
                  <div className="w-14 h-14 flex-shrink-0 bg-white overflow-hidden flex items-center justify-center">
                    {item.imageUrl ? (
                      <img src={mediaUrl(item.imageUrl)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <FaImage className="text-neutral-200" size={18} />
                    )}
                  </div>
                  <span className="text-neutral-700 flex-1">
                    {item.productName} <span className="text-neutral-500">× {item.quantity}</span>
                  </span>
                  <span className="text-brand-primary whitespace-nowrap">{formatPrice(item.lineTotal)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-neutral-200 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-neutral-700">
                <span>Međuzbir</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-neutral-700">
                <span>Dostava</span>
                <span>{shippingLabel}</span>
              </div>
              <div className="flex justify-between text-lg font-medium text-brand-primary pt-2">
                <span>Ukupno</span>
                <span data-testid="order-total">{formatPrice(order.total)}</span>
              </div>
            </div>

            <p className="text-sm text-neutral-500 mt-6">
              Način plaćanja: pouzeće — iznos plaćate kuriru pri preuzimanju.
            </p>
          </div>

          <div className="text-center mt-10">
            <Link to="/shop" className="btn-accent inline-block">Nastavite kupovinu</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderSuccessPage;
